import { Box, Tooltip } from "@mui/material";
import colors from "../../../../theme/Colours";
import {
  LargeBoldText,
  RegularText,
  SemiBoldText,
  StyledKpiCard,
} from "../styles";
import OutletDialog, {
  OutletRow,
} from "../../../../common/outletDialog/outletDialog";
import { useState, useCallback } from "react";

interface OrderIntervalValue {
  account_type: string;
  show_details: any[];
  order_qty: string;
  last_order_since: string;
  freq: string;
}

interface OrderIntervalDetail {
  period: string;
  name: string;
  values: OrderIntervalValue[];
}

interface OrderIntervalsAnalysis {
  key: string;
  name: string;
  unit: string;
  details: OrderIntervalDetail[];
  isPeriodOneDataExists: boolean;
  isPeriodTwoDataExists: boolean;
}

interface AccountActivityDetailsProps {
  orderIntervalsAnalysis?: OrderIntervalsAnalysis;
}

export default function AccountActivityDetails({
  orderIntervalsAnalysis,
}: AccountActivityDetailsProps) {
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedAccountType, setSelectedAccountType] = useState<string>("");

  const getShowDetailsForAccountType = useCallback(
    (accountType: string): OutletRow[] => {
      if (!orderIntervalsAnalysis?.details) return [];

      const currentPeriod = orderIntervalsAnalysis.details.find(
        (detail) => detail.period === "current"
      );

      if (!currentPeriod?.values) return [];

      const accountValue = currentPeriod.values.find(
        (value) => value.account_type === accountType
      );

      const details = accountValue?.show_details ?? [];

      return details.map((detail: any) => ({
        outletName: (detail.client_name || "").toString().trim(),
        email: (detail.email || "").toString().trim(),
      }));
    },
    [orderIntervalsAnalysis]
  );

  const handleAccountTypeClick = (accountType: string) => {
    setSelectedAccountType(accountType);
    setDialogOpen(true);
  };

  const handleDialogClose = () => {
    setDialogOpen(false);
    setSelectedAccountType("");
  };

  const dialogRows = getShowDetailsForAccountType(selectedAccountType);

  const getDialogTitle = () => {
    if (!selectedAccountType) return "";

    switch (selectedAccountType) {
      case "Active":
        return "Active Accounts";
      case "Lapsed":
        return "Lapsed Accounts";
      case "In-Active":
        return "In-Active Accounts";
      default:
        return `${selectedAccountType} Accounts`;
    }
  };
  // If no orderIntervalsAnalysis, this means we're not in sales-analytics mode
  // Render a placeholder to maintain layout stability
  if (!orderIntervalsAnalysis) {
    return (
      <StyledKpiCard
        sx={{
          display: "flex",
          flexDirection: "column",
          padding: "6px 0px",
          height: "100%",
          minHeight: "85px",
          visibility: "hidden",
        }}
      />
    );
  }
  // Extract account activity numbers from order intervals analysis
  const getAccountActivityNumbers = () => {
    if (!orderIntervalsAnalysis?.details) {
      return { active: 0, lapsed: 0, inactive: 0 };
    }

    const currentPeriod = orderIntervalsAnalysis.details.find(
      (detail) => detail.period === "current"
    );

    if (!currentPeriod?.values) {
      return { active: 0, lapsed: 0, inactive: 0 };
    }

    const active =
      currentPeriod.values.find((value) => value.account_type === "Active")
        ?.order_qty || "0";

    const lapsed =
      currentPeriod.values.find((value) => value.account_type === "Lapsed")
        ?.order_qty || "0";

    const inactive =
      currentPeriod.values.find((value) => value.account_type === "In-Active")
        ?.order_qty || "0";

    return {
      active: parseInt(active, 10) || 0,
      lapsed: parseInt(lapsed, 10) || 0,
      inactive: parseInt(inactive, 10) || 0,
    };
  };

  const { active, lapsed, inactive } = getAccountActivityNumbers();
  return (
    <StyledKpiCard
      sx={{
        display: "flex",
        flexDirection: "column",
        padding: "6px 0px",
        height: "100%",
        minHeight: "85px",
      }}
    >
      <SemiBoldText
        color="#1a1a18"
        sx={{ fontSize: "14px", fontWeight: 600, px: 2, pb: 0.5 }}
      >
        Account Activity Details
      </SemiBoldText>

      <Box sx={{ display: "flex", flexDirection: "column", gap: 0 }}>
        {/* Active Accounts */}
        <Tooltip title="ordered in last 30 days" arrow>
          <Box
            onClick={() => handleAccountTypeClick("Active")}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "1px 16px",
              margin: "0 10px 2px",
              borderRadius: "8px",
              background: "#e6f1fb",
              border: "1px solid #b5d4f4",
              cursor: "pointer",
              transition: "filter 0.15s",
              "&:hover": {
                filter: "brightness(0.95)",
              },
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <RegularText
                sx={{ fontSize: "11px", fontWeight: 600, color: "#0C447C" }}
              >
                Active Accounts
              </RegularText>
            </Box>
            <LargeBoldText
              sx={{ fontSize: "18px", fontWeight: 700, color: "#185FA5" }}
            >
              {active}
            </LargeBoldText>
          </Box>
        </Tooltip>

        {/* Lapsed Accounts */}
        <Tooltip title="30-90 days since last order" arrow>
          <Box
            onClick={() => handleAccountTypeClick("Lapsed")}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "1px 16px",
              margin: "0 10px 2px",
              borderRadius: "8px",
              background: "#d6f0e8",
              border: "1px solid #9FE1CB",
              cursor: "pointer",
              transition: "filter 0.15s",
              "&:hover": {
                filter: "brightness(0.95)",
              },
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <RegularText
                sx={{ fontSize: "11px", fontWeight: 600, color: "#085041" }}
              >
                Lapsed Accounts
              </RegularText>
            </Box>
            <LargeBoldText
              sx={{ fontSize: "18px", fontWeight: 700, color: "#0F6E56" }}
            >
              {lapsed}
            </LargeBoldText>
          </Box>
        </Tooltip>

        {/* In-Active Accounts */}
        <Tooltip title="90+days since last order" arrow>
          <Box
            onClick={() => handleAccountTypeClick("In-Active")}
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "1px 16px",
              margin: "0 10px 4px",
              borderRadius: "8px",
              background: "#fde8e0",
              border: "1px solid #f5c4b3",
              cursor: "pointer",
              transition: "filter 0.15s",
              "&:hover": {
                filter: "brightness(0.95)",
              },
            }}
          >
            <Box sx={{ display: "flex", flexDirection: "column", gap: "2px" }}>
              <RegularText
                sx={{ fontSize: "11px", fontWeight: 600, color: "#712B13" }}
              >
                In-Active Accounts
              </RegularText>
            </Box>
            <LargeBoldText
              sx={{ fontSize: "18px", fontWeight: 700, color: "#993C1D" }}
            >
              {inactive}
            </LargeBoldText>
          </Box>
        </Tooltip>
      </Box>

      <OutletDialog
        open={dialogOpen}
        onClose={handleDialogClose}
        rows={dialogRows}
        emptyMessage={
          selectedAccountType
            ? `No ${selectedAccountType.toLowerCase()} accounts found`
            : "No accounts"
        }
        maxWidth="md"
      />
    </StyledKpiCard>
  );
}
